// RecentAwardsWidget.jsx
// Shows recent awards given across the club

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrophy, FaArrowRight, FaMedal, FaStar, FaHeart } from 'react-icons/fa';
import { useUser } from '../../../context/UserContext';
import styles from './RecentAwardsWidget.module.css';

const RecentAwardsWidget = ({
  clubId,
  programs
}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch recent awards
  useEffect(() => {
    const fetchAwards = async () => {
      if (!programs || programs.length === 0 || !user) {
        setLoading(false);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const allAwards = [];
        
        // Get auth token
        const token = await user.getIdToken();
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        for (const program of programs) {
          try {
            const response = await fetch(`${backendUrl}/api/awards/program/${program._id}`, { headers });
            if (!response.ok) continue;
            
            const data = await response.json();
            const programAwards = (data.awards || data || []).map(award => ({
              ...award,
              programName: program.name,
              programId: program._id
            }));
            allAwards.push(...programAwards);
          } catch (e) {
            // Skip if awards fetch fails
          }
        }

        // Sort by date and take recent
        allAwards.sort((a, b) => new Date(b.awardedAt || b.createdAt) - new Date(a.awardedAt || a.createdAt));
        setAwards(allAwards.slice(0, 6));
      } catch (error) {
        console.error('Error fetching awards:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAwards();
  }, [programs, user]);

  const getAwardIcon = (award) => {
    const type = award.awardTemplateId?.awardType || award.type || award.category;
    switch (type?.toLowerCase()) {
      case 'individual':
      case 'mvp':
        return <FaTrophy className={styles.awardIconGold} />;
      case 'statistical':
      case 'achievement':
        return <FaStar className={styles.awardIconBlue} />;
      case 'character':
      case 'sportsmanship':
        return <FaHeart className={styles.awardIconPink} />;
      case 'team':
      case 'position':
      default:
        return <FaMedal className={styles.awardIconBronze} />;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getRecipientName = (award) => {
    // Check for recipients array (standard award structure)
    if (award.recipients && Array.isArray(award.recipients) && award.recipients.length > 0) {
      const firstRecipient = award.recipients[0];
      const name = firstRecipient.playerName || firstRecipient.name || 'Unknown';
      const moreCount = award.recipients.length - 1;
      return moreCount > 0 ? `${name} +${moreCount}` : name;
    }
    // Fallback checks
    if (award.recipientName) return award.recipientName;
    const recipient = award.recipient || award.player;
    if (!recipient) return 'Unknown';
    if (typeof recipient === 'string') return recipient;
    return recipient.firstName 
      ? `${recipient.firstName} ${recipient.lastName || ''}`.trim()
      : recipient.playerName || recipient.name || 'Unknown';
  };

  const getAwardTitle = (award) => {
    // Check for awardTemplateId structure
    if (award.awardTemplateId) {
      return award.awardTemplateId.awardName || award.awardTemplateId.name || 'Award';
    }
    return award.title || award.name || award.type || 'Award';
  };

  const handleViewProgram = (programId) => {
    navigate(`/program/${programId}/awards`);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaTrophy className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Recent Awards</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  if (awards.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaTrophy className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Recent Awards</h3>
        </div>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>🏆</span>
          <p>No awards given yet</p>
          <span className={styles.emptySubtext}>Recognize outstanding players!</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaTrophy className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Recent Awards</h3>
        <span className={styles.totalCount}>{awards.length} recent</span>
      </div>

      <div className={styles.content}>
        <div className={styles.awardsList}>
          {awards.map((award, index) => (
            <div 
              key={award._id || index} 
              className={styles.awardItem}
              onClick={() => handleViewProgram(award.programId)}
            >
              <div className={styles.awardIcon}>
                {getAwardIcon(award)}
              </div>
              <div className={styles.awardDetails}>
                <span className={styles.awardTitle}>
                  {getAwardTitle(award)}
                </span>
                <span className={styles.awardRecipient}>
                  {getRecipientName(award)}
                </span>
                <span className={styles.awardMeta}>
                  {award.programName} • {formatDate(award.awardedAt || award.createdAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.footerLink} onClick={() => navigate(`/clubs/${clubId}`)}>
          View All Awards <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default RecentAwardsWidget;

